import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, MapPin, Home, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

// Helper to format date as YYYY-MM-DD in local timezone (not UTC)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PlannedActions = ({ actions = [], yards = [], hives = [], queenBatches = [], onAddAction, onRemoveAction, onMarkDone }) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [newActionText, setNewActionText] = useState('');
  const [selectedYard, setSelectedYard] = useState('');
  const [selectedHive, setSelectedHive] = useState('');
  const [localActions, setLocalActions] = useState(actions);
  useEffect(() => { setLocalActions(actions); }, [actions]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const handleAddAction = () => {
    if (!newActionText.trim()) return;
    
    // Find selected yard and hive details
    const yard = yards.find(y => y.id === selectedYard);
    const hive = hives.find(h => h.id === selectedHive);
    
    // Use local date string (YYYY-MM-DD) to avoid timezone issues
    const newAction = {
      id: Date.now(),
      description: newActionText.trim(),
      title: newActionText.trim(),
      date: formatDateLocal(selectedDate),
      createdAt: new Date().toISOString(),
      status: 'pending',
      yard_id: selectedYard || null,
      yard: yard?.name || null,
      hive_id: selectedHive || null,
      hive_number: hive?.hive_number || null
    };
    
    setLocalActions(prev => [...prev, newAction]);
    onAddAction?.(newAction);
    setNewActionText('');
    setSelectedYard('');
    setSelectedHive('');
  };

  // Filter hives by selected yard
  const filteredHives = selectedYard 
    ? hives.filter(h => h.yard === yards.find(y => y.id === selectedYard)?.name)
    : hives;

  const handleRemoveAction = (actionId) => {
    setLocalActions(prev => prev.filter(a => a.id !== actionId));
    onRemoveAction?.(actionId);
  };

  const handleMarkDone = (action) => {
    const updatedAction = {
      ...action,
      status: 'completed',
      completedAt: new Date().toISOString()
    };
    setLocalActions(prev => prev.map(a => a.id === action.id ? updatedAction : a));
    onMarkDone?.(updatedAction);
  };

  // Filter actions for selected date - compare using local date strings
  const selectedDateStr = formatDateLocal(selectedDate);
  const actionsForDate = localActions.filter(action => {
    // Handle both YYYY-MM-DD format and ISO string format
    let actionDateStr = action.date;
    if (action.date && action.date.includes('T')) {
      // If it's an ISO string, parse and convert to local date
      const d = new Date(action.date);
      actionDateStr = formatDateLocal(d);
    }
    return actionDateStr === selectedDateStr && action.status !== 'completed';
  });

  // Get queen rearing events for selected date (only batch-level milestones)
  const queenEventsForDate = [];
  queenBatches.filter(b => !['complete', 'archived', 'failed'].includes(b.status) && b.graft_date).forEach(batch => {
    const gp = batch.graft_date.split('-');
    const graftDate = new Date(parseInt(gp[0]), parseInt(gp[1]) - 1, parseInt(gp[2]));
    const milestones = [
      { days: 0, label: 'Graft', action: 'Graft day - Start grafting' },
      { days: 1, label: 'Finisher', action: 'Move cells to Finisher hive' },
      { days: 4, label: 'Check Accepted', action: 'Check cell acceptance rate' },
      { days: 8, label: 'Capped', action: 'Check cells - should be capped' },
      { days: 10, label: 'Transfer', action: 'Transfer to incubator or mating NUCs' },
      { days: 16, label: 'Emergence', action: 'Queens should be emerging' },
      { days: 23, label: 'Mating Flights', action: 'Mating flights begin' },
      { days: 28, label: 'Check Laying', action: 'Check if queens are laying' },
    ];
    
    milestones.forEach(m => {
      const eventDate = new Date(graftDate);
      eventDate.setDate(eventDate.getDate() + m.days);
      if (formatDateLocal(eventDate) === selectedDateStr) {
        queenEventsForDate.push({
          id: `queen-${batch.id}-${m.label}`,
          batchName: batch.batch_name || `Batch ${batch.id.slice(0,6)}`,
          batchId: batch.id,
          label: m.label,
          action: m.action
        });
      }
    });
  });

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm" data-testid="planned-actions">
      <div className="p-4 border-b border-stone-100">
        <h2 className="font-bold text-black text-lg">Planned Actions</h2>
      </div>

      {/* Date Navigation */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-stone-100">
        <button 
          onClick={() => navigateDate(-1)}
          className="p-1 hover:bg-stone-100 rounded"
        >
          <ChevronLeft className="h-5 w-5 text-stone-500" />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-lg">📅</span>
          <span className="font-medium text-black">{formatDate(selectedDate)}</span>
        </div>
        <button 
          onClick={() => navigateDate(1)}
          className="p-1 hover:bg-stone-100 rounded"
        >
          <ChevronRight className="h-5 w-5 text-stone-500" />
        </button>
      </div>

      {/* Actions List */}
      <div className="p-4 space-y-3">
        {/* Queen Rearing Events */}
        {queenEventsForDate.length > 0 && (
          <div className="space-y-2 pb-3 border-b border-pink-100">
            {queenEventsForDate.map((event) => (
              <div 
                key={event.id}
                onClick={() => navigate('/queen-rearing?tab=batches')}
                className="flex items-center justify-between bg-pink-50 border border-pink-200 rounded-lg p-3 cursor-pointer hover:bg-pink-100"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Crown className="h-5 w-5 text-pink-600" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-pink-800 block truncate">{event.action}</span>
                    <span className="text-xs text-pink-600">{event.batchName}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Regular Planned Actions */}
        {actionsForDate.length > 0 ? (
          actionsForDate.map((action) => (
            <div 
              key={action.id}
              className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-amber-600">✏️</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-black block truncate">{action.description || action.title}</span>
                  {(action.hive_number || action.yard) && (
                    <span className="text-xs text-stone-500 flex items-center gap-2 mt-0.5">
                      {action.hive_number && (
                        <span className="flex items-center gap-0.5">
                          <Home className="h-3 w-3" />
                          Hive {action.hive_number}
                        </span>
                      )}
                      {action.yard && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />
                          {action.yard}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 ml-2">
                <Button
                  size="sm"
                  onClick={() => handleMarkDone(action)}
                  className="h-8 px-3 text-xs font-semibold bg-green-500 hover:bg-green-600 text-white"
                >
                  Done
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemoveAction(action.id)}
                  className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        ) : queenEventsForDate.length === 0 && (
          <div className="text-center py-4 text-stone-500 text-sm">
            No actions planned for this day
          </div>
        )}

        {/* Add Action Input */}
        <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
          {/* Yard and Hive Selection */}
          <div className="flex gap-2">
            <div className="flex-1">
              <select
                value={selectedYard}
                onChange={(e) => {
                  setSelectedYard(e.target.value);
                  setSelectedHive(''); // Reset hive when yard changes
                }}
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm text-black focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="planned-action-yard-select"
              >
                <option value="">📍 All Yards</option>
                {yards.map((yard) => (
                  <option key={yard.id} value={yard.id}>
                    {yard.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <select
                value={selectedHive}
                onChange={(e) => setSelectedHive(e.target.value)}
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm text-black focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="planned-action-hive-select"
              >
                <option value="">🏠 All Hives</option>
                {filteredHives.map((hive) => (
                  <option key={hive.id} value={hive.id}>
                    Hive {hive.hive_number}{hive.yard ? ` - ${hive.yard}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Action Text Input */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Add action for this day..."
                value={newActionText}
                onChange={(e) => setNewActionText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddAction()}
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm text-black placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="planned-action-input"
              />
            </div>
            <Button
              size="sm"
              onClick={handleAddAction}
              disabled={!newActionText.trim()}
              className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-black disabled:opacity-50"
              data-testid="planned-action-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlannedActions;
